/**
 * Property & Water System Information — step-by-step intake (refined 2026-02).
 *
 * Shows one question at a time with Next/Back navigation and a progress bar.
 * Used inside Booking Step 2 (`embedded=true`) and on the Passport property
 * page (via the edit modal in `PropertyInfoCard`).
 *
 * Step list (with conditional skipping based on prior answers):
 *  1. Intended water use (multi-select)  — ALWAYS first (Feb 2026 Agricultural
 *     Water Suitability Assessment intake). Optional, defaults to household
 *     drinking only.
 *  1a. What will you be irrigating?       — only if garden/agricultural irrigation selected
 *  1b. Which livestock?                   — only if livestock watering selected
 *  2. Tank Types & quantities (or `None`)
 *  3. Tank cleaned in last 5 years?  — skipped if `None` AND test isn't tank-based
 *  4. Filtration system
 *  5. UV treatment?                    — skipped if filtration none/unsure
 *  6. Calcite filter?                  — skipped if filtration none/unsure
 *  7. Experienced issues (multi)
 *  8. Home older than 10 years?
 *  9. Summary (review + edit)
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Edit3 } from "lucide-react";

const TANK_TYPES = [
  { k: "poly", l: "Poly Tanks" },
  { k: "concrete", l: "Concrete Tanks" },
  { k: "steel", l: "Steel Tanks" },
  { k: "fibreglass", l: "Fibreglass Tanks" },
];
const YES_NO = [
  { v: "yes", l: "Yes" }, { v: "no", l: "No" }, { v: "unsure", l: "Unsure" },
];
const FILTRATION = [
  { v: "whole_house", l: "Whole House Filtration System" },
  { v: "under_sink", l: "Under Sink Filtration System" },
  { v: "both", l: "Both" },
  { v: "none", l: "No Filtration System" },
  { v: "unsure", l: "Unsure" },
];
const FILTER_REPLACED = [
  { v: "lt_6m", l: "Within 6 months" },
  { v: "6_12m", l: "6–12 months" },
  { v: "gt_12m", l: "More than 12 months ago" },
  { v: "never", l: "Never replaced" },
  { v: "unsure", l: "Unsure" },
];
const ISSUES = [
  { v: "bad_taste", l: "Bad taste" },
  { v: "bad_smell", l: "Bad smell" },
  { v: "cloudy", l: "Cloudy water" },
  { v: "sediment", l: "Sediment" },
  { v: "staining", l: "Staining" },
  { v: "low_ph", l: "Low pH concerns" },
  { v: "iron_staining", l: "Iron staining" },
  { v: "filter_blocking", l: "Filter blocking" },
  { v: "none", l: "No known issues" },
];
const HOME_LABEL = { yes: "More than 10 years old", no: "Less than 10 years old", unsure: "Unsure" };
const FILTRATION_LABEL = Object.fromEntries(FILTRATION.map((o) => [o.v, o.l]));
const FILTER_REPL_LABEL = Object.fromEntries(FILTER_REPLACED.map((o) => [o.v, o.l]));
const ISSUE_LABEL = Object.fromEntries(ISSUES.map((o) => [o.v, o.l]));
const YN_LABEL = { yes: "Yes", no: "No", unsure: "Unsure" };

const BORE_USE = [
  { v: "household_potable", l: "Household / drinking use" },
  { v: "garden_stock_only", l: "Garden, irrigation or stock only" },
  { v: "unsure", l: "Unsure / mixed use" },
];
const BORE_USE_LABEL = Object.fromEntries(BORE_USE.map((o) => [o.v, o.l]));

// Feb 2026 — Agricultural Water Suitability Assessment intake. A SEPARATE,
// new multi-select field from `BORE_USE`/`primary_water_use` above —
// those drive the existing Bore 360 drinking-water framing only and are
// never touched by this feature. Selecting an agricultural use here does
// NOT change the test panel or imply agricultural suitability has been
// assessed — see `agricultural_assessment.py` on the backend.
const AG_USES = [
  { v: "household_drinking", l: "Household / drinking water" },
  { v: "garden_irrigation", l: "Irrigation — gardens & landscaping" },
  { v: "agricultural_irrigation", l: "Irrigation — agricultural crops" },
  { v: "livestock_watering", l: "Livestock drinking water" },
  { v: "general_farm_use", l: "General farm use" },
  { v: "other", l: "Other / not specified" },
];
const AG_USE_LABEL = Object.fromEntries(AG_USES.map((o) => [o.v, o.l]));
const IRRIGATION_CATEGORIES = [
  { v: "not_specified", l: "Not sure / general" },
  { v: "general_garden_landscaping", l: "Garden & landscaping" },
  { v: "vegetable_crops", l: "Vegetable crops" },
  { v: "pasture_fodder", l: "Pasture / fodder" },
  { v: "orchard_vine_tree_crops", l: "Orchard, vine or tree crops" },
  { v: "broadacre_field_crops", l: "Broadacre / field crops" },
];
const IRRIGATION_CATEGORY_LABEL = Object.fromEntries(IRRIGATION_CATEGORIES.map((o) => [o.v, o.l]));
const LIVESTOCK_SPECIES_OPTS = [
  { v: "cattle_beef", l: "Beef cattle" },
  { v: "cattle_dairy", l: "Dairy cattle" },
  { v: "sheep_adult", l: "Sheep (adult)" },
  { v: "sheep_weaner", l: "Sheep (weaner/lamb)" },
  { v: "horses", l: "Horses" },
  { v: "pigs", l: "Pigs" },
  { v: "poultry", l: "Poultry" },
  { v: "goats", l: "Goats" },
  { v: "not_specified", l: "Not sure / other" },
];
const LIVESTOCK_SPECIES_LABEL = Object.fromEntries(LIVESTOCK_SPECIES_OPTS.map((o) => [o.v, o.l]));
// Mirrors backend `agricultural_assessment.IRRIGATION_MANDATORY_PARAMS` /
// `LIVESTOCK_MANDATORY_PARAMS` against `test_catalog.py` — the "basic"
// (4-Point) package has neither chloride nor TDS, so it can never
// support a full irrigation/livestock conclusion today.
const AG_TESTING_SUFFICIENT_PACKAGES = new Set(["tank", "bore"]);

const TANK_BASED_TESTS = new Set(["tank"]);

const EMPTY = {
  tanks: { poly: 0, concrete: 0, steel: 0, fibreglass: 0, none: false },
  filtration: "",
  uv: "", calcite: "", filter_last_replaced: "",
  // Phase 2L-A63 — default tank-cleaned-in-last-5-years to "no". Most
  // households haven't had a professional tank clean within 5 years,
  // and defaulting to "no" surfaces the appropriate cleaning rec for
  // those customers instead of silently dropping it. Customer can
  // still flip to yes/unsure. Field is also auto-cleared (line ~133)
  // when the customer selects "no tanks", so this default is harmless
  // for non-tank households.
  tank_cleaned_5y: "no",
  bore_use: "",
  experienced_issues: [],
  // Feb 2026 — Agricultural Water Suitability Assessment. Optional;
  // defaults to household drinking only so every existing booking
  // workflow keeps working unchanged if never touched.
  agricultural_intended_uses: ["household_drinking"],
  irrigation_category: "not_specified",
  livestock_species: [],
  // Phase 2L-A39 — default "Is your home more than 10 years old?" to "yes".
  // Older homes are more likely to trigger BioCleanse / pipework-related
  // recommendations when paired with other conditions; defaulting to "yes"
  // matches the more common Sunshine Coast housing stock and lets the
  // customer change it if their home is newer.
  home_older_than_10y: "yes",
  notes: "",
};


export function PropertyIntakeForm({ value, onChange, initial, embedded = false, testIdPrefix = "intake", testId = null }) {
  const [v, setV] = useState(() => ({ ...EMPTY, ...(initial || value || {}) }));
  const [stepIdx, setStepIdx] = useState(0);
  const [reviewing, setReviewing] = useState(false);

  // Sync upward on every change
  useEffect(() => { onChange && onChange(v); /* eslint-disable-next-line */ }, [v]);

  const hasTanks = !v.tanks?.none && TANK_TYPES.some((t) => (parseInt(v.tanks?.[t.k] || 0) > 0));
  // Tank cleaning question follows the customer's tank answer directly — if
  // they selected "None", we trust them and hide the cleaning question.
  const showTankCleaning = hasTanks;
  const showFilterDetails = v.filtration && v.filtration !== "none" && v.filtration !== "unsure";

  // Build the step list dynamically based on conditional logic
  const isBoreTest = testId === "bore";
  const wantsIrrigation = (v.agricultural_intended_uses || []).some((u) => u === "garden_irrigation" || u === "agricultural_irrigation");
  const wantsLivestock = (v.agricultural_intended_uses || []).includes("livestock_watering");
  const steps = useMemo(() => {
    const list = [
      { id: "agricultural_intended_uses", title: "What will this water be used for?",
        hint: "Select all that apply. This does not change your test panel or your lab results — it only determines which sections appear in your report." },
    ];
    if (wantsIrrigation) list.push({ id: "irrigation_category", title: "What will you be irrigating?",
      hint: "Helps us compare your results against the right crop/soil sensitivity reference — not required." });
    if (wantsLivestock) list.push({ id: "livestock_species", title: "Which livestock will drink this water?",
      hint: "Guideline values differ by species — select all that apply, or 'Not sure / other' if unknown." });
    if (isBoreTest) list.push({ id: "bore_use", title: "How is this bore water used?",
      hint: "This helps us tailor treatment recommendations to how the water is actually used." });
    list.push({ id: "tanks", title: "What type & qty of water tanks are on the property?",
      hint: "Enter the number of each tank type. If a tank type is not present, leave it blank or enter 0." });
    if (showTankCleaning) list.push({ id: "tank_cleaned_5y", title: "Has the water tank been cleaned in the last 5 years?" });
    list.push({ id: "filtration", title: "Is your drinking water filtered?" });
    if (showFilterDetails) {
      list.push({ id: "uv", title: "Does your filtration system include ultraviolet (UV) treatment?" });
      list.push({ id: "calcite", title: "Does your filtration system include a Calcite Filter?" });
      // `filter_last_replaced` was retired Feb-2026 — see PropertyIntakeForm
      // schema notes. Field stays nullable on the model + payload scrubber
      // below for back-compat with historical bookings.
    }
    list.push({ id: "experienced_issues", title: "Have you experienced any of the following?", hint: "Select all that apply." });
    list.push({ id: "home_older_than_10y", title: "Is your home more than 10 years old?" });
    return list;
  }, [showTankCleaning, showFilterDetails, isBoreTest, wantsIrrigation, wantsLivestock]);

  // Clamp current step if conditional logic shortened the list
  useEffect(() => {
    if (stepIdx > steps.length - 1 && !reviewing) setStepIdx(Math.max(0, steps.length - 1));
  }, [steps.length, stepIdx, reviewing]);

  const currentStep = steps[stepIdx];
  const totalSteps = steps.length + 1; // +1 for review
  const displayedIdx = reviewing ? steps.length : stepIdx;

  const set = (k, val) => setV((x) => ({ ...x, [k]: val }));
  const setTankCount = (mat, val) => setV((x) => ({
    ...x,
    tanks: { ...x.tanks, none: false, [mat]: Math.max(0, parseInt(val || 0)) },
  }));
  const toggleTankNone = (checked) => setV((x) => ({
    ...x,
    tanks: checked
      ? { poly: 0, concrete: 0, steel: 0, fibreglass: 0, none: true }
      : { ...x.tanks, none: false },
    tank_cleaned_5y: checked ? "" : x.tank_cleaned_5y,
  }));
  const toggleIssue = (val) => setV((x) => {
    const has = x.experienced_issues.includes(val);
    return { ...x, experienced_issues: has ? x.experienced_issues.filter((i) => i !== val) : [...x.experienced_issues, val] };
  });
  // Feb 2026 — at least one intended use must always remain selected
  // (defaults to household_drinking); never allow an empty array.
  const toggleAgUse = (val) => setV((x) => {
    const current = x.agricultural_intended_uses || [];
    const has = current.includes(val);
    const next = has ? current.filter((i) => i !== val) : [...current, val];
    return { ...x, agricultural_intended_uses: next.length ? next : current };
  });
  const toggleLivestockSpecies = (val) => setV((x) => {
    const current = x.livestock_species || [];
    const has = current.includes(val);
    return { ...x, livestock_species: has ? current.filter((i) => i !== val) : [...current, val] };
  });

  const canAdvance = (step) => {
    if (!step) return false;
    if (step.optional) return true;
    const id = step.id;
    if (id === "tanks") {
      return v.tanks?.none || TANK_TYPES.some((t) => parseInt(v.tanks?.[t.k] || 0) > 0);
    }
    if (id === "experienced_issues") return true; // multi-select, allow empty
    if (id === "agricultural_intended_uses") return (v.agricultural_intended_uses || []).length > 0;
    if (id === "irrigation_category") return true; // optional, has a safe default
    if (id === "livestock_species") return true; // optional — empty means "not sure / other"
    return Boolean(v[id]);
  };

  const next = () => {
    if (!canAdvance(currentStep)) return;
    if (stepIdx >= steps.length - 1) { setReviewing(true); return; }
    setStepIdx(stepIdx + 1);
  };
  const back = () => {
    if (reviewing) { setReviewing(false); setStepIdx(steps.length - 1); return; }
    setStepIdx(Math.max(0, stepIdx - 1));
  };
  const jumpTo = (i) => { setReviewing(false); setStepIdx(i); };

  return (
    <div data-testid={`${testIdPrefix}-form`} className={embedded ? "" : "bg-white rounded-3xl border border-slate-200 p-6 lg:p-8"}>
      {!embedded && (
        <div className="mb-5">
          <h2 className="font-display text-xl font-bold text-slate-900">Property & Water System Information</h2>
          <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
            This information helps us provide more accurate recommendations and build a complete AWARE Water Health Record for your property.
          </p>
        </div>
      )}

      {/* Progress bar */}
      <div className="flex items-center gap-3" data-testid={`${testIdPrefix}-progress`}>
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 flex-shrink-0">
          {reviewing ? "Review" : `Step ${stepIdx + 1} of ${steps.length}`}
        </div>
        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-cyan-500 transition-all duration-300"
            style={{ width: `${((displayedIdx + 1) / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Step body */}
      <div className="mt-5 min-h-[220px]" data-testid={`${testIdPrefix}-step-${reviewing ? "review" : currentStep?.id}`}>
        {reviewing ? (
          <ReviewPanel v={v} steps={steps} onEdit={jumpTo} testIdPrefix={testIdPrefix} />
        ) : (
          <StepBody
            step={currentStep} v={v} set={set}
            setTankCount={setTankCount} toggleTankNone={toggleTankNone}
            toggleIssue={toggleIssue} toggleAgUse={toggleAgUse} toggleLivestockSpecies={toggleLivestockSpecies}
            testId={testId}
            testIdPrefix={testIdPrefix}
          />
        )}
      </div>

      {/* Nav buttons */}
      <div className="mt-6 flex items-center justify-between">
        <button
          type="button" onClick={back}
          disabled={stepIdx === 0 && !reviewing}
          data-testid={`${testIdPrefix}-back-btn`}
          className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {reviewing ? (
          <div className="text-sm text-emerald-700 font-semibold inline-flex items-center gap-1.5">
            <Check className="w-4 h-4" /> Looks good — continue below to confirm details
          </div>
        ) : (
          <button
            type="button" onClick={next}
            disabled={!canAdvance(currentStep)}
            data-testid={`${testIdPrefix}-next-btn`}
            className="inline-flex items-center gap-1.5 px-5 h-10 rounded-full bg-slate-900 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {stepIdx >= steps.length - 1 ? "Review answers" : "Next"} <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}


// ---------- Step body ----------
function StepBody({ step, v, set, setTankCount, toggleTankNone, toggleIssue, toggleAgUse, toggleLivestockSpecies, testId, testIdPrefix }) {
  if (!step) return null;
  const id = step.id;
  const wantsIrrigation = (v.agricultural_intended_uses || []).some((u) => u === "garden_irrigation" || u === "agricultural_irrigation");
  const wantsLivestock = (v.agricultural_intended_uses || []).includes("livestock_watering");
  const testingGapWarning = (id === "agricultural_intended_uses" && (wantsIrrigation || wantsLivestock) && testId && !AG_TESTING_SUFFICIENT_PACKAGES.has(testId))
    ? "Additional Testing Required — the selected test package does not include every parameter needed for a full agricultural suitability assessment. The agricultural section of your report will show \"Not Assessed / Insufficient Data\" until a tank or bore chemistry panel (or further testing) is completed."
    : null;
  return (
    <div>
      <h3 className="font-display text-base lg:text-lg font-bold text-slate-900">
        {step.title}
      </h3>
      {step.hint && <p className="mt-1 text-xs text-slate-500">{step.hint}</p>}
      <div className="mt-4">
        {id === "agricultural_intended_uses" && (
          <div className="space-y-2">
            <div className="grid sm:grid-cols-2 gap-2">
              {AG_USES.map((opt) => {
                const sel = (v.agricultural_intended_uses || []).includes(opt.v);
                return (
                  <label key={opt.v} data-testid={`${testIdPrefix}-aguse-${opt.v}`}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors text-sm ${
                      sel ? "border-cyan-500 bg-cyan-50 text-cyan-900 font-medium" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                    }`}>
                    <input type="checkbox" checked={sel} onChange={() => toggleAgUse(opt.v)}
                      className="rounded text-cyan-600 focus:ring-cyan-500" />
                    {opt.l}
                  </label>
                );
              })}
            </div>
            {testingGapWarning && (
              <div data-testid={`${testIdPrefix}-ag-testing-gap-warning`}
                className="mt-2 flex items-start gap-2 px-3 py-2.5 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 text-xs leading-relaxed">
                <span>{testingGapWarning}</span>
              </div>
            )}
          </div>
        )}
        {id === "irrigation_category" && (
          <Radios name="irrigation_category" value={v.irrigation_category} options={IRRIGATION_CATEGORIES.map((o) => ({ v: o.v, l: o.l }))} onChange={(val) => set("irrigation_category", val)} testIdPrefix={`${testIdPrefix}-irrigation-category`} />
        )}
        {id === "livestock_species" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {LIVESTOCK_SPECIES_OPTS.map((opt) => {
              const sel = (v.livestock_species || []).includes(opt.v);
              return (
                <label key={opt.v} data-testid={`${testIdPrefix}-livestock-${opt.v}`}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors text-sm ${
                    sel ? "border-cyan-500 bg-cyan-50 text-cyan-900 font-medium" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                  }`}>
                  <input type="checkbox" checked={sel} onChange={() => toggleLivestockSpecies(opt.v)}
                    className="rounded text-cyan-600 focus:ring-cyan-500" />
                  {opt.l}
                </label>
              );
            })}
          </div>
        )}
        {id === "tanks" && (
          <div className="space-y-3">
            <div className={`grid sm:grid-cols-2 gap-2 ${v.tanks?.none ? "opacity-40 pointer-events-none" : ""}`}>
              {TANK_TYPES.map(({ k, l }) => (
                <label key={k} data-testid={`${testIdPrefix}-tank-${k}`} className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border border-slate-200 bg-white">
                  <span className="text-sm text-slate-700">{l}</span>
                  <input
                    type="number" min={0} max={20}
                    value={v.tanks?.[k] ?? 0}
                    onChange={(e) => setTankCount(k, e.target.value)}
                    className="w-16 h-9 rounded-lg border border-slate-200 px-2 text-sm text-right"
                  />
                </label>
              ))}
            </div>
            <label data-testid={`${testIdPrefix}-tank-none`} className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors text-sm ${
              v.tanks?.none ? "border-cyan-500 bg-cyan-50 text-cyan-900 font-medium" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
            }`}>
              <input type="checkbox" checked={!!v.tanks?.none}
                onChange={(e) => toggleTankNone(e.target.checked)}
                className="rounded text-cyan-600 focus:ring-cyan-500" />
              None — no water tanks on this property
            </label>
          </div>
        )}
        {(id === "tank_cleaned_5y" || id === "uv" || id === "calcite" || id === "home_older_than_10y") && (
          <Radios name={id} value={v[id]} options={YES_NO} onChange={(val) => set(id, val)} testIdPrefix={`${testIdPrefix}-${id}`} />
        )}
        {id === "filtration" && (
          <Radios name="filtration" value={v.filtration} options={FILTRATION} onChange={(val) => set("filtration", val)} testIdPrefix={`${testIdPrefix}-filtration`} />
        )}
        {id === "bore_use" && (
          <Radios name="bore_use" value={v.bore_use} options={BORE_USE.map((o) => ({ v: o.v, l: o.l }))} onChange={(val) => set("bore_use", val)} testIdPrefix={`${testIdPrefix}-bore-use`} />
        )}
        {id === "filter_last_replaced" && (
          <Radios name="filter_last_replaced" value={v.filter_last_replaced} options={FILTER_REPLACED} onChange={(val) => set("filter_last_replaced", val)} testIdPrefix={`${testIdPrefix}-filter-replaced`} />
        )}
        {id === "experienced_issues" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {ISSUES.map((opt) => {
              const sel = v.experienced_issues.includes(opt.v);
              return (
                <label key={opt.v} data-testid={`${testIdPrefix}-issue-${opt.v}`}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors text-sm ${
                    sel ? "border-cyan-500 bg-cyan-50 text-cyan-900 font-medium" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                  }`}>
                  <input type="checkbox" checked={sel} onChange={() => toggleIssue(opt.v)}
                    className="rounded text-cyan-600 focus:ring-cyan-500" />
                  {opt.l}
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


// ---------- Review panel ----------
function ReviewPanel({ v, steps, onEdit, testIdPrefix }) {
  return (
    <div data-testid={`${testIdPrefix}-review`}>
      <h3 className="font-display text-base lg:text-lg font-bold text-slate-900">Review your property info</h3>
      <p className="mt-1 text-xs text-slate-500">Tap any row to edit your answer.</p>
      <div className="mt-4 divide-y divide-slate-100 rounded-2xl border border-slate-200 overflow-hidden">
        {steps.map((s, i) => (
          <button key={s.id} type="button"
            data-testid={`${testIdPrefix}-review-edit-${s.id}`}
            onClick={() => onEdit(i)}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors">
            <div>
              <div className="text-xs font-semibold text-slate-500">{s.title}</div>
              <div className="text-sm text-slate-900 mt-0.5">{displayAnswer(s.id, v)}</div>
            </div>
            <Edit3 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}


function displayAnswer(id, v) {
  if (id === "agricultural_intended_uses") {
    const sel = v.agricultural_intended_uses || [];
    return sel.length ? sel.map((k) => AG_USE_LABEL[k] || k).join(", ") : "Household / drinking water";
  }
  if (id === "irrigation_category") return IRRIGATION_CATEGORY_LABEL[v.irrigation_category] || "Not sure / general";
  if (id === "livestock_species") {
    const sel = v.livestock_species || [];
    return sel.length ? sel.map((k) => LIVESTOCK_SPECIES_LABEL[k] || k).join(", ") : "Not specified";
  }
  if (id === "tanks") {
    if (v.tanks?.none) return "No tanks on this property";
    const parts = TANK_TYPES.filter((t) => parseInt(v.tanks?.[t.k] || 0) > 0)
      .map((t) => `${v.tanks[t.k]} ${t.l.replace(" Tanks", "")}`);
    return parts.length ? parts.join(", ") : "—";
  }
  if (id === "filtration") return FILTRATION_LABEL[v.filtration] || "—";
  if (id === "bore_use") return BORE_USE_LABEL[v.bore_use] || "—";
  if (id === "filter_last_replaced") return FILTER_REPL_LABEL[v.filter_last_replaced] || "Skipped";
  if (id === "experienced_issues") return v.experienced_issues.length
    ? v.experienced_issues.map((k) => ISSUE_LABEL[k] || k).join(", ")
    : "No issues reported";
  if (id === "home_older_than_10y") return HOME_LABEL[v.home_older_than_10y] || "—";
  return YN_LABEL[v[id]] || "—";
}


function Radios({ name, value, options, onChange, testIdPrefix }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
      {options.map((opt) => {
        const sel = value === opt.v;
        return (
          <label key={opt.v} data-testid={`${testIdPrefix}-${opt.v}`}
            className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors text-sm ${
              sel ? "border-cyan-500 bg-cyan-50 text-cyan-900 font-medium" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
            }`}>
            <input type="radio" name={name} checked={sel} onChange={() => onChange(opt.v)}
              className="text-cyan-600 focus:ring-cyan-500" />
            {opt.l}
          </label>
        );
      })}
    </div>
  );
}


// ---------- Public helpers ----------
export function intakeFormToPayload(form) {
  const out = { ...form };
  // Normalize tanks
  if (!out.tanks) out.tanks = { poly: 0, concrete: 0, steel: 0, fibreglass: 0, none: false };
  if (out.tanks.none) {
    out.tanks = { poly: 0, concrete: 0, steel: 0, fibreglass: 0, none: true };
    out.tank_cleaned_5y = null;
  }
  // Drop filter sub-questions when filtration absent
  if (out.filtration === "none" || out.filtration === "unsure") {
    out.uv = null; out.calcite = null; out.filter_last_replaced = null;
  } else {
    if (!out.uv) out.uv = null;
    if (!out.calcite) out.calcite = null;
    if (!out.filter_last_replaced) out.filter_last_replaced = null;
  }
  // `filter_last_replaced` is no longer asked — drop from payload (back-compat scrub)
  delete out.filter_last_replaced;
  // `filtration_age` is no longer asked — drop from payload (back-compat scrub)
  delete out.filtration_age;
  if (!out.tank_cleaned_5y) out.tank_cleaned_5y = null;
  if (!out.bore_use) out.bore_use = null;
  if (!out.home_older_than_10y) out.home_older_than_10y = null;
  if (!out.notes) out.notes = null;
  // Sanitize removed legacy fields
  delete out.water_source;
  delete out.drinking_tank_count;
  delete out.home_age;
  // Feb 2026 — Agricultural Water Suitability Assessment: clear
  // sub-answers that aren't relevant to the uses actually selected, so
  // stale answers from a prior selection never linger in the payload.
  const uses = out.agricultural_intended_uses || ["household_drinking"];
  out.agricultural_intended_uses = uses.length ? uses : ["household_drinking"];
  const wantsIrrigation = uses.some((u) => u === "garden_irrigation" || u === "agricultural_irrigation");
  if (!wantsIrrigation) out.irrigation_category = null;
  else if (!out.irrigation_category) out.irrigation_category = "not_specified";
  if (!uses.includes("livestock_watering")) out.livestock_species = [];
  return out;
}


export function validateIntakeForm(form) {
  if (!form) return "Please complete the Property & Water System Information section";
  // Tanks: must have at least one quantity OR the None checkbox
  const hasAny = ["poly", "concrete", "steel", "fibreglass"].some((k) => parseInt(form.tanks?.[k] || 0) > 0);
  if (!form.tanks?.none && !hasAny) return "Tell us about water tanks (enter quantities or tick None)";
  if (!form.filtration) return "Please tell us about filtration";
  if (!form.home_older_than_10y) return "Please tell us whether the home is more than 10 years old";
  return null;
}
